import {
  QueryClient,
  type InvalidateQueryFilters,
} from "@tanstack/react-query";
import {
  createSentryMutationCache,
  createSentryQueryCache,
} from "./sentry-query";

/**
 * Fires after any react-query mutation succeeds (RTK Query mutations are bridged
 * in through the store middleware). Listeners register only while they are
 * mounted, so a screen with nothing listening pays nothing.
 */
export const mutationSuccessListeners = new Set<() => void>();
export const notifyMutationSuccess = () => {
  mutationSuccessListeners.forEach((listener) => listener());
};

export const queryClient = new QueryClient({
  queryCache: createSentryQueryCache(),
  mutationCache: createSentryMutationCache({
    onSuccess: notifyMutationSuccess,
  }),
  defaultOptions: {
    queries: {
      // Data freshness: Consider data fresh for 5 minutes before marking as stale
      // This prevents unnecessary refetches while still keeping data reasonably up-to-date
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Cache retention: Keep inactive queries in cache for 10 minutes
      // Should be longer than staleTime to allow background refetching
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

      // Smart retry logic: Don't retry on client errors (4xx), retry up to 3 times for server errors
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number })?.status;
        // Don't retry on client errors (4xx) - these are usually permanent
        if (status && status >= 400 && status < 500) {
          return false;
        }
        // Retry up to 3 times for server errors (5xx) or network errors
        return failureCount < 3;
      },

      // Exponential backoff: Wait progressively longer between retries
      // Prevents overwhelming the server with rapid retry attempts
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Max 30 seconds

      // Refetch behavior: Optimized for editor applications
      refetchOnWindowFocus: false, // Don't refetch when user switches tabs (better UX for editors)
      refetchOnMount: true, // Always refetch when component mounts (ensures fresh data)
      refetchOnReconnect: true, // Refetch when network reconnects (important for offline support)

      // Network mode: Only run queries when online
      // Prevents failed requests and error states when offline
      networkMode: "online",
    },
    mutations: {
      // Smart retry for mutations: Don't retry on client errors (4xx)
      // Mutations are typically user actions, so we want to fail fast on client errors
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number })?.status;
        // Don't retry on client errors (4xx) - user input issues shouldn't be retried
        if (status && status >= 400 && status < 500) {
          return false;
        }
        // Retry once for server errors (5xx) or network errors
        return failureCount < 1;
      },

      // Network mode: Only run mutations when online
      networkMode: "online",
    },
  },
});

// Query client methods for manual operations
export const reactQueryUtils = {
  invalidateQueries: (
    queryKey: string[],
    options?: Omit<InvalidateQueryFilters, "queryKey">,
  ) => {
    return queryClient.invalidateQueries({
      queryKey,
      ...options,
    });
  },

  setQueryData: <T>(queryKey: unknown[], data: T) => {
    return queryClient.setQueryData(queryKey, data);
  },

  getQueryData: <T>(queryKey: unknown[]) => {
    return queryClient.getQueryData<T>(queryKey);
  },

  /**
   * Every cached query under a key prefix, as [key, data] pairs.
   *
   * List queries are keyed by their params — ["pages", "list", { …filters }] —
   * so an optimistic update has to reach all of them, and needs each key to know
   * what that list was asking for.
   */
  getQueriesData: <T>(queryKey: unknown[]) => {
    return queryClient.getQueriesData<T>({ queryKey });
  },

  removeQueries: (queryKey: unknown[]) => {
    return queryClient.removeQueries({ queryKey });
  },

  cancelQueries: (queryKey: unknown[]) => {
    return queryClient.cancelQueries({ queryKey });
  },

  clear: () => {
    return queryClient.clear();
  },
};
